import React, { useState, useCallback, useEffect, useRef } from 'react';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import { useSwipeable } from 'react-swipeable';

// ─── Zoom controls (must live inside a TransformWrapper context) ──────────────

function LightboxZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => zoomIn()}
        title="Zoom in"
        className="w-7 h-7 flex items-center justify-center rounded bg-[#21262d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors text-sm font-mono"
      >
        +
      </button>
      <button
        onClick={() => zoomOut()}
        title="Zoom out"
        className="w-7 h-7 flex items-center justify-center rounded bg-[#21262d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors text-sm font-mono"
      >
        −
      </button>
      <button
        onClick={() => resetTransform()}
        title="Reset view"
        className="w-7 h-7 flex items-center justify-center rounded bg-[#21262d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function FigureLightbox({ figures, initialIndex, revealCaption = true, onClose }) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  // Ref to the TransformWrapper so we can read the current zoom scale before
  // deciding whether a swipe should navigate slides or pan the zoomed image.
  const transformRef = useRef(null);

  const activeFig = figures[activeIndex];
  const hasMultiple = figures.length > 1;

  // Functional state updates: next/prev don't close over activeIndex, so they
  // are stable and don't need to be listed as keyboard-effect dependencies.
  const next = useCallback(
    () => setActiveIndex((i) => (i + 1) % figures.length),
    [figures.length],
  );

  const prev = useCallback(
    () => setActiveIndex((i) => (i - 1 + figures.length) % figures.length),
    [figures.length],
  );

  // ── Body scroll lock ───────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ── Keyboard navigation ────────────────────────────────────────────────────
  // Depends only on stable refs — no activeIndex, no unnecessary re-binding.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, onClose]);

  // ── Touch swipe — disabled when zoomed in so the user can pan instead ──────
  const swipeHandlers = useSwipeable({
    onSwipedLeft:  () => { if ((transformRef.current?.state.scale ?? 1) <= 1) next(); },
    onSwipedRight: () => { if ((transformRef.current?.state.scale ?? 1) <= 1) prev(); },
    delta: 50,
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: false,
  });

  return (
    // Backdrop — click anywhere to close
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 animate-fade-in"
      onClick={onClose}
    >

      {/* ── Close button ─────────────────────────────────────────────── */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close lightbox"
        className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-[#161b22]/90 border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* ── Figure counter badge ──────────────────────────────────────── */}
      {hasMultiple && (
        <div className="absolute top-4 left-4 z-10 text-xs font-mono px-3 py-1.5 rounded-full bg-[#161b22]/90 border border-[#30363d] text-[#8b949e] select-none">
          {activeIndex + 1} / {figures.length}
        </div>
      )}

      {/* ── Prev arrow ───────────────────────────────────────────────── */}
      {hasMultiple && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous figure"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-[#161b22]/90 border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* ── Image container — stopPropagation prevents backdrop close ── */}
      <div
        className="relative flex flex-col overflow-hidden rounded-lg border border-[#30363d] shadow-2xl"
        style={{ maxWidth: '90vw', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
        {...swipeHandlers}
      >
        {/*
          key={activeIndex} resets the TransformWrapper on every slide change,
          clearing zoom/pan state so the next figure always starts at scale 1.
        */}
        <TransformWrapper
          key={activeIndex}
          ref={transformRef}
          initialScale={1}
          minScale={0.5}
          maxScale={12}
          centerOnInit
          wheel={{ step: 0.08 }}
          doubleClick={{ mode: 'toggle' }}
        >
          {/* Zoom controls bar */}
          <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-[#0d1117] border-b border-[#30363d]">
            <span className="text-[10px] font-mono text-[#8b949e] select-none">
              Scroll · Drag · Pinch to zoom
            </span>
            <LightboxZoomControls />
          </div>

          <TransformComponent
            wrapperStyle={{
              width: '90vw',
              maxHeight: 'calc(85vh - 41px)',
              overflow: 'hidden',
            }}
            contentStyle={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
            }}
          >
            <img
              src={activeFig.file_path}
              alt={activeFig.alt_text}
              style={{ maxWidth: '100%', maxHeight: 'calc(85vh - 73px)', objectFit: 'contain' }}
              draggable={false}
            />
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* ── Next arrow ───────────────────────────────────────────────── */}
      {hasMultiple && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next figure"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-[#161b22]/90 border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* ── Caption bar ──────────────────────────────────────────────── */}
      <div
        className="fixed bottom-0 left-0 right-0 px-6 py-3 bg-[#0d1117]/95 border-t border-[#30363d]"
        onClick={(e) => e.stopPropagation()}
      >
        {revealCaption ? (
          <p className="text-xs font-mono text-[#8b949e] text-center leading-relaxed">
            {activeFig.description}
          </p>
        ) : (
          <p className="text-xs font-mono text-[#484f58] italic text-center leading-relaxed">
            Figure description is hidden during the exam and will be revealed once your answer has been marked.
          </p>
        )}
      </div>

    </div>
  );
}
